import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EquipmentIcon, MultiSelect, WarningIcon, dayjs } from '@umanforge/common';

import { EditBoxIcon } from '@common_components/icons/editBox.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';

import BaseModal from '../../modal/components/base_modal.component';
import Tabs, { TabItem } from '@common_components/buttons/Tabs.component';
import { ModifyTask, PostTask } from 'interfaces/task.interface';
import SelectInput from '@common_components/inputs/selects/selectInput.component';
import { Tooltip } from 'react-tooltip';
import { SectorSettings } from 'interfaces/setting.interface';
import InputTime from 'components/inputs/selectTime.component';
import { useTaskModal } from '../hook/taskModal.hook';
import DatePickerSoloInput from '@common_components/inputs/datePickerInputSolo.component';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import BottomModal from '@common_components/modals/bottomModal.component';
import RecurrenceModal from './recurrenceModal.component';
import { RecurrenceSVG } from '@common_components/svg/recurrence.svg';
import RoleTaskForm from 'features/role_task/components/roleTaskForm.component';

interface TaskModalProps {
  onTaskCreate?: ( task: PostTask ) => Promise<string> | null;
  onUpdate?: ( task: ModifyTask ) => Promise<boolean>;
  onDeleteTask?: ( taskid: string ) => void | undefined;
  onClose: () => void;
  planningId: string;
  sectorId: string;
  tableNameProps?: string;
  taskId?: string;
  timeStartDrag?: dayjs.Dayjs;
  timeEndDrag?: dayjs.Dayjs;
  dateDrag?: dayjs.Dayjs;
  settings?: SectorSettings;
}

const TaskModal: React.FC<TaskModalProps> = ( {
  onTaskCreate,
  onDeleteTask,
  onClose,
  onUpdate,
  taskId,
  planningId,
  sectorId,
  timeStartDrag,
  timeEndDrag,
  dateDrag,
  settings
} ) => {
  const { t } = useTranslation();
  const [ updateRecurrence, setUpdateRecurrence ] = useState( false );

  const {
    errors,
    selectedLocation, setSelectedLocation,
    selectedTaskAction, setSelectedTaskAction,
    dateStart,
    dateEnd,
    timeStart, setTimeStart,
    timeEnd, setTimeEnd,
    handleChangeDateStart, handleChangeDateEnd,
    recurrenceSaved,
    recurrenceChanged,
    recurrenceValid,
    activeTab, setActiveTab,
    isDeleteModalOpen, setIsDeleteModalOpen,
    isDeleteRecurrenceModalOpen, setIsDeleteRecurrenceModalOpen,
    isConfirmationModalOpen, setIsConfirmationModalOpen,
    isFormValid,
    isFormChanged,
    hasTaskSpecificChanges,
    locations,
    taskActions,
    selectedEquipments, setSelectedEquipments,
    handleSubmit,
    handleDelete,
    handleCloseModal,
    fetchLocation,
    fetchTaskAction,
    calendarStep,
    equipmentAbilities,
    equipmentSkills,
    equipmentsOptions,
    fullDay, setFullDay,
    recurrenceModalOpen, setRecurrenceModalOpen,
    //recurrenceChanged, setRecurrenceChanged,
    handleRecurrenceChange,
    getEquipmentErrors,
    onRoleTaskChange,
    requiredEquiAbilities,
    requiredEquiSkills,
    dateAndTimeStart
  } = useTaskModal(
    planningId,
    sectorId,
    onClose,
    taskId,
    timeStartDrag,
    timeEndDrag,
    dateDrag,
    settings,
    onTaskCreate,
    onUpdate,
    onDeleteTask,
  );

  const pages: TabItem[] = [
    { id: 'main', name: t( 'translation:main' ) },
    { id: 'roletasks', name: t( 'translation:attribution' ) },
  ];

  const hasGlobalRecurrenceChange = recurrenceChanged || hasTaskSpecificChanges();
  const isSaveRecurrenceDisabled = !recurrenceModalOpen || !recurrenceSaved || !recurrenceValid || !isFormValid || !hasGlobalRecurrenceChange;

  // Render
  return (
    <>
      <BaseModal
        isOpen={true}
        onClose={handleCloseModal}
        noConfirmationModal={!isFormChanged}
        icon={taskId ? <EditBoxIcon className="bg-blue-200 p-2 rounded-md" size={10} /> : <PlusIcon className="bg-green-200 p-2 rounded-md" size={10} />}
        title={taskId ? t( 'translation:edit_task' ) : t( 'translation:create_task' )}
        footer={
          <BottomModal
            tableId={taskId}
            formValid={isFormValid}
            onCreate={() => { handleSubmit( updateRecurrence ); } }
            onUpdate={() => { handleSubmit( updateRecurrence ); } }
            onDelete={() => {return setIsDeleteModalOpen( true );}}
            extraButtons={
              ( recurrenceModalOpen && recurrenceSaved ) ?
                [ {
                  label: t( 'translation:save_for_all_mission' ),
                  icon: <RecurrenceSVG className='w-5' stroke='white' strokeWidth={0.5}/>,
                  onClick: () => {
                    setUpdateRecurrence( true );
                    handleSubmit( true );
                  },
                  textColor: 'text-white',
                  bgColor: 'bg-app-color hover:bg-blue-600',
                  borderColor: `${!true && 'border-app-color'}`,
                  hoverBorderColor: 'hover:border-blue-600',
                  width: 'w-fit mx-1',
                  disable: isSaveRecurrenceDisabled
                } ]
                :
                []
            }
            direction='right'
          />
        }
      >
        <div className="py-5 px-7 gap-y-4 bg-white rounded-md shadow-md font-texte flex-col flex">
          <Tabs items={pages} activeItem={activeTab} onItemClick={setActiveTab} />
          <div className="relative">
            <div className={'py-5 px-7 gap-y-4 h-[38rem] bg-white overflow-auto flex-col rounded-b-xl border font-texte flex'}>
              <div className={ activeTab === 0 ? '' : 'hidden' }>
                <SelectInput
                  className='w-full'
                  labelPosition='left'
                  placeholder={ t( 'translation:task' ) }
                  label={ t( 'translation:task' ) }
                  fetchOptions={fetchTaskAction}
                  onSelect={ setSelectedTaskAction }
                  selected={ selectedTaskAction}
                  refreshTrigger={ taskActions.length }
                  isRequired
                />
                <p className="text-red-500 h-6 -mt-4">{ errors.taskAction ?? ''}</p>

                <SelectInput
                  className='w-full'
                  labelPosition='left'
                  placeholder={ t( 'translation:location' ) }
                  label={ t( 'translation:location' ) }
                  fetchOptions={fetchLocation}
                  onSelect={ setSelectedLocation }
                  selected={ selectedLocation }
                  refreshTrigger={ locations.length }
                  isRequired
                />
                <p className="text-red-500 h-6 -mt-4">{ errors.location ?? ''}</p>


                <div className='flex space-x-3 mt-5'>
                  <div className={ `w-4/5 ${ fullDay ? 'lg:flex gap-x-5' : 'flex-col' }` }>
                    <div className='w-full'>
                      <div className={ `${ fullDay ? 'flex-col gap-x-5' : 'lg:flex' }` }>
                        <p className={ `text-gray-600 mr-4 left mt-4 ${ fullDay ? 'w-full' : 'w-4/12'}` }> { t( 'translation:beginning_of_task' ) } </p>
                        <div className='flex gap-x-2 my-2 w-full'>
                          <div className='w-full'>
                            <DatePickerSoloInput
                              label={ undefined }
                              defaultValue={ dateStart }
                              onChange={ handleChangeDateStart }
                              textSize={'w-3/4'}
                            />
                          </div>
                          <div className={`w-fit ${ fullDay && 'hidden' }`}>
                            <InputTime
                              step={calendarStep}
                              min={ dayjs( timeStart ).startOf( 'day' ) }
                              max={ dayjs( timeStart ).endOf( 'day' ) }
                              value={ timeStart }
                              onChange={ setTimeStart }
                              isRequired
                            />
                          </div>
                        </div>
                      </div>
                      { errors.startHours && <p className="text-red-500 h-6 -mt-1">{ errors.startHours }</p> }
                    </div>

                    <div className='w-full'>
                      <div className={ `${ fullDay ? 'flex-col gap-x-5' : 'lg:flex' }` }>
                        <p className={`text-gray-600 mr-4 left mt-4 ${ fullDay ? 'w-full' : 'w-4/12'}` }> { t( 'translation:end_of_task' ) } </p>
                        <div className='flex gap-x-2 my-2 w-full'>
                          <div className='w-full'>
                            <DatePickerSoloInput
                              label={ undefined }
                              defaultValue={ dateEnd}
                              onChange={ handleChangeDateEnd }
                              textSize={'w-3/4'}
                            />
                          </div>
                          <div className={`w-fit ${ fullDay && 'hidden' }`}>
                            <InputTime
                              step={calendarStep}
                              min={ dayjs( timeEnd ).startOf( 'day' ) }
                              max={ dayjs( timeEnd ).endOf( 'day' ) }
                              value={ timeEnd }
                              onChange={ setTimeEnd }
                              isRequired
                            />
                          </div>
                        </div>
                      </div>
                      { errors.endHours && <p className="text-red-500 h-6 -mt-1">{ errors.endHours }</p> }
                    </div>
                  </div>

                  <div className='flex flex-col w-1/5 h-fit mt-4' id={'fullDay'} >
                    <p className={'text-gray-600 w-full flex-center mr-4 left'}> { t( 'translation:full_day' ) } </p>
                    <div className='w-full flex items-center'>
                      <div className='w-full px-3' >
                        <div className="px-2 pt-2 right w-full justify-end flex items-center">
                          <label className="w-full flex-center items-center cursor-pointer" onClick={ () => { setFullDay( !fullDay ); } }>
                            <input
                              type="checkbox"
                              value=""
                              className="sr-only peer"
                              name={'day_switch'}
                              checked={fullDay}
                              readOnly
                            />
                            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                        <Tooltip
                          anchorSelect={ '#fullDay' }
                          className='z-[1000] max-w-[600px] text-wrap'
                          place="top"
                          positionStrategy='fixed'
                        >
                          <span>
                            <span className='text-[14px] font-semibold underline underline-offset-2'> { t( 'translation:full_day' ) } </span>
                            <p className='pl-6 pt-1'> { t( 'translation:is_the_task_on_the_day' ) } </p>
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    <p className="h-6 -mt-1"/>
                  </div>
                </div>


                <div className={'flex mt-5 mb-2'}>
                  <p className={'text-gray-600 w-3/12 mr-4 left mt-2.5'}> { t( 'common:equipments' ) + ' (' + selectedEquipments.length + ')' } </p>
                  <MultiSelect
                    disabled={ false }
                    placeholder={ t( 'translation:no_associated_equipments' ) }
                    icon={ <EquipmentIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedEquipments }
                    getValues={ equipmentsOptions }
                    onChange={ setSelectedEquipments }
                    extra={ equipment => {
                      const { isExpired, skillRequired, abilityRequired } = getEquipmentErrors( equipment, requiredEquiAbilities, requiredEquiSkills );

                      return <div className='flex h-6' >
                        { ( isExpired || skillRequired || abilityRequired ) && <>
                          <div id={ 'equipment-' + equipment.object.id }>
                            <WarningIcon color='rgb(255, 36, 36)'/>
                          </div>
                          <Tooltip
                            anchorSelect={ '#equipment-' + equipment.object.id }
                            className='z-[1000] max-w-[600px] text-wrap'
                            place="top"
                            positionStrategy='fixed'
                          >
                            <span>
                              { isExpired && <span className='text-[14px] font-semibold'> { t( 'translation:errors.equipment_expiration_date_expired' ) } </span> }
                              { skillRequired && <span className='text-[14px] font-semibold'> { t( 'translation:errors.required_equipment_skills' ) } </span> }
                              { abilityRequired && <span className='text-[14px] font-semibold'> { t( 'translation:errors.required_equipment_authorizations' ) } </span> }
                            </span>
                          </Tooltip>
                        </>
                        }
                      </div>;
                    } }
                  />
                </div>
                <div className='-mt-3'>
                  { errors.requiredEquiSkills && <p className="text-red-500 h-6 -mt-2">{ errors.requiredEquiSkills }</p> }
                  { errors.requiredEquiAbilities && <p className="text-red-500 h-6 -mt-2">{ errors.requiredEquiAbilities }</p> }
                  { errors.equipmentExpirationDate && <p className="text-red-500 h-6 -mt-2">{ errors.equipmentExpirationDate }</p>
                  }
                </div>

                <div className='mt-8'>
                  {/* <div className='flex w-1/3 h-fit' id={'fullDay'} >
                    <p className={'text-gray-600 w-full flex items-center left'}> { t( 'translation:recurrence' ) } </p>
                    <div className='w-full flex items-center'>
                      <div className='w-full px-3' >
                        <div className="px-2 pt-2 right w-full justify-end flex items-center">
                          <label className="w-full flex-center items-center cursor-pointer" onClick={ () => { setRecurrenceModalOpen( !recurrenceModalOpen ); } }>
                            <input
                              type="checkbox"
                              value=""
                              className="sr-only peer"
                              name={'day_switch'}
                              checked={ recurrenceSaved != null || recurrenceModalOpen }
                              readOnly
                            />
                            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <p className="h-6 -mt-1"/>
                  </div> */}

                  <RecurrenceModal
                    onSave={handleRecurrenceChange}
                    sectorId={sectorId}
                    taskId={taskId}
                    recurrence={recurrenceSaved}
                    taskDateStart={dateAndTimeStart ?? dayjs()}
                    isOpen={recurrenceModalOpen}
                    onRecurrenceOpen={setRecurrenceModalOpen}
                  />
                </div>
              </div>

              <div className={ activeTab === 1 ? '' : 'hidden' }>
                <RoleTaskForm
                  sectorId={sectorId}
                  taskId={taskId}
                  onChange={ onRoleTaskChange }
                  equipmentSkillsOption={equipmentSkills}
                  equipmentAbilitiesOption={equipmentAbilities}
                />
              </div>
            </div>
          </div>
        </div>
      </BaseModal>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={ () => {
          if ( recurrenceSaved && recurrenceModalOpen ) {
            setIsDeleteModalOpen( false );
            setIsDeleteRecurrenceModalOpen( true );
          }
          else {
            handleDelete();
          }
        } }
        onCancel={() => { return setIsDeleteModalOpen( false );}}
        onClose={ () => { return setIsDeleteModalOpen( false ); } }
        title={ t( 'translation:delete_task' ) }
        description={ t( 'translation:are_you_sure_that_you_want_to_delete_task_it_is_irreversible' ) }
      />

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onConfirm={() => {
          errors.equipmentExpirationDate = undefined;
          handleSubmit( updateRecurrence );
        }}
        onCancel={() => { return setIsConfirmationModalOpen( false ); } }
        onClose={ () => { return setIsConfirmationModalOpen( false ); } }
        title={ t( 'translation:errors.equipment_expiration_date_expired' ) }
        description={ t( 'translation:are_you_sure_that_you_want_to_use_this_task_equipment' ) }
      />

      <ConfirmationModal
        isOpen={isDeleteRecurrenceModalOpen}
        onConfirm={ handleDelete }
        onCancel={() => { handleDelete( true ); }}
        onClose={ () => { setIsDeleteRecurrenceModalOpen( false ); } }
        confirmLabel={t( 'translation:task_only' )}
        confirmColor='gray'
        cancelLabel={t( 'translation:save_for_all_mission' )}
        title={ t( 'translation:delete_recurrence_task' ) }
        description={ t( 'translation:do_you_want_to_delete_the_task_only_or_all_the_tasks_on_the_recurrence' ) }
        icon={<RecurrenceSVG className='w-8' stroke='dark-gray'/>}
      />
    </>
  );
};

export default TaskModal;
